import React, {type ChangeEventHandler, useCallback, useContext, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {clearSelection} from 'src/components/data-table-selection';
import {MAX_SELECTED_ENTITIES} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {
    userSearchResultStateActions,
    userSearchResultStateSelector,
} from 'src/modules/profile/account/tabs/users-and-groups/users-groups-search-result';
import {HEADERS, SORTING_WARNING} from 'src/modules/user-search-result/constants';
import {SortDirection, SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {UserSortField as UserSortFields} from 'src/openapi/admin/models/UserSortField';

import {SortHeaderFilter} from '@pb/kit-component-filters';
import Group from '@pb/kit-component-group';
import {Input} from '@pb/kit-component-input-v2';
import Label from '@pb/kit-component-label';


import {COLUMNS, SELECTION_KEY} from '../../constants';
import {
    userSearchResultResultSelector,
} from '../../slices/user-search-result.slice';

import {
    HeaderSkeleton,
} from './header-skeleton';

type Props = {
    totalCount: number,
};

export const JobTitleHeaderController = ({totalCount}: Props) => {
    const dispatch = useDispatch();
    const {setErrorMessage} = useContext(MessageContext);

    const cacheKeys = useSelector(userSearchResultStateSelector);

    const [searchQuery, setSearchQuery] = useState<string>(cacheKeys.searchCriteria.jobTitle as string ?? '');

    const sort = useCallback((direction: ('ASC' | 'DESC')) => {
        if (totalCount > MAX_SELECTED_ENTITIES) {
            setErrorMessage(SORTING_WARNING);
            return;
        }

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(userSearchResultStateActions.setSortCriteria({
            direction: direction as SortDirection,
            field: UserSortFields.JOB_TITLE,
        }));
    }, [totalCount, dispatch, setErrorMessage]);

    const applyFilter = useCallback(() => {
        const {
            jobTitle: _jobTitle,
            ...otherSearchCriteria
        } = cacheKeys.searchCriteria;

        const searchCriteria = {
            ...otherSearchCriteria,
            ...searchQuery ? {jobTitle: searchQuery} : undefined,
        };

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(userSearchResultStateActions.setSearchCriteria(searchCriteria));
    }, [cacheKeys.searchCriteria, searchQuery, dispatch]);

    const clearFilter = useCallback(() => {
        const {
            jobTitle: _jobTitle,
            ...otherSearchCriteria
        } = cacheKeys.searchCriteria;

        setSearchQuery('');

        dispatch(clearSelection(SELECTION_KEY));
        dispatch(userSearchResultStateActions.setSearchCriteria(otherSearchCriteria));
    }, [cacheKeys.searchCriteria, dispatch, setSearchQuery]);

    const cancelFilter = useCallback(() => {
        setSearchQuery(cacheKeys.searchCriteria.jobTitle as string);
    }, [cacheKeys.searchCriteria, setSearchQuery]);

    const onSearch = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
        const enteredValue = event.currentTarget.value;
        setSearchQuery(enteredValue);
    }, []);

    return (
        <SortHeaderFilter
            filterActive={!!cacheKeys.searchCriteria.jobTitle}
            sortable={true}
            caption={HEADERS[COLUMNS.JOB_TITLE]}
            direction={cacheKeys.sortCriteria.field === UserSortFields.JOB_TITLE
                ? cacheKeys.sortCriteria.direction
                : null}
            defaultDirection={SortDirections.ASC}
            onSort={sort}
            onApply={applyFilter}
            onReset={clearFilter}
            onCancel={cancelFilter}>
            <Group direction="v" style={{width: '100%'}}>

                <Label>Search for Job Title</Label>
                <Input value={searchQuery} onChange={onSearch}/>
            </Group>
        </SortHeaderFilter>
    );
};

export const JobTitleHeader = () => {
    const result = useSelector(userSearchResultResultSelector);

    return result ? (
        <JobTitleHeaderController
            totalCount={result.totalCount}
        />
    ) : (
        <HeaderSkeleton columnId={COLUMNS.GROUP_ID}/>
    );
};
